import { Box, Button, ButtonBase, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { formatPrice } from '@/components/billing/paywallCopy';
import { useHomeHQPlans, useStartHomeHQCheckout } from '@/hooks/useHomeHQAccess';
import { HomeHQInterval, HomeHQPlan } from '@/models/Subscription';

const PLAN_LABELS: Record<HomeHQPlan, { title: string; homes: string }> = {
  single: { title: 'HomeHQ', homes: '1 home' },
  multi: { title: 'HomeHQ Multi-Property', homes: 'Up to 5 homes' },
};

/**
 * Compact plan chooser sized for the 400px auth-style card: interval toggle,
 * two selectable plans, promo code (monthly only), one checkout button.
 */
export const PlanPicker = ({ ctaLabel = 'Subscribe now' }: { ctaLabel?: string }) => {
  const { data: plans, isLoading, isError } = useHomeHQPlans();
  const checkout = useStartHomeHQCheckout();
  const [interval, setBillingInterval] = useState<HomeHQInterval>('month');
  const [plan, setPlan] = useState<HomeHQPlan>('single');
  const [promoCode, setPromoCode] = useState('');

  const visiblePlans = useMemo(
    () => (plans ?? []).filter((p) => p.interval === interval),
    [plans, interval],
  );
  const monthlyByPlan = useMemo(
    () =>
      Object.fromEntries(
        (plans ?? []).filter((p) => p.interval === 'month').map((p) => [p.plan, p.unitAmount]),
      ) as Partial<Record<HomeHQPlan, number>>,
    [plans],
  );
  const selected = visiblePlans.find((p) => p.plan === plan) ?? visiblePlans[0];

  useEffect(() => {
    if (selected && selected.plan !== plan) setPlan(selected.plan);
  }, [selected, plan]);

  const errorMessage = (checkout.error as any)?.response?.data?.message as string | undefined;

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Loading plans…
      </Typography>
    );
  }
  if (isError || !selected) {
    return (
      <Typography variant="body2" color="error" textAlign="center">
        Plans are unavailable right now. Please try again shortly.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Interval toggle */}
      <Box
        role="group"
        aria-label="Billing interval"
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          p: '3px',
          borderRadius: '12px',
          bgcolor: 'var(--tx-fill)',
        }}>
        {(['month', 'year'] as HomeHQInterval[]).map((value) => (
          <ButtonBase
            key={value}
            onClick={() => setBillingInterval(value)}
            aria-pressed={interval === value}
            sx={{
              py: 0.75,
              borderRadius: '10px',
              fontSize: 13,
              fontWeight: 600,
              color: interval === value ? 'primary.contrastText' : 'text.secondary',
              bgcolor: interval === value ? 'primary.main' : 'transparent',
            }}>
            {value === 'month' ? 'Monthly' : 'Annual'}
          </ButtonBase>
        ))}
      </Box>

      {/* Plans */}
      <Stack spacing={1} role="radiogroup" aria-label="Plan">
        {visiblePlans.map((option) => {
          const label = PLAN_LABELS[option.plan];
          const isSelected = option.lookupKey === selected.lookupKey;
          const monthly = monthlyByPlan[option.plan];
          const savings =
            option.interval === 'year' && monthly
              ? Math.round((1 - option.unitAmount / (monthly * 12)) * 100)
              : 0;
          return (
            <ButtonBase
              key={option.lookupKey}
              role="radio"
              aria-checked={isSelected}
              onClick={() => setPlan(option.plan)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                gap: 2,
                px: 2,
                py: 1.5,
                borderRadius: '14px',
                border: 0,
                bgcolor: isSelected ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
              }}>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{label.title}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  {label.homes}
                  {option.limits.seats ? ` · ${option.limits.seats} people` : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                  {formatPrice(option.unitAmount, option.currency)}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    / {option.interval === 'month' ? 'mo' : 'yr'}
                  </Typography>
                </Typography>
                {savings > 0 && (
                  <Typography variant="caption" sx={{ color: '#c0ff02' }}>
                    Save {savings}%
                  </Typography>
                )}
              </Box>
            </ButtonBase>
          );
        })}
      </Stack>

      {/* Promo code: monthly only (validated again server-side) */}
      {interval === 'month' ? (
        <TextField
          size="small"
          placeholder="Promo code (optional)"
          aria-label="Promo code"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
          slotProps={{ htmlInput: { style: { fontSize: 14 } } }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Promo codes apply to monthly plans.
        </Typography>
      )}

      <Button
        fullWidth
        sx={{ fontSize: 14 }}
        disabled={checkout.isPending}
        onClick={() =>
          checkout.mutate({
            lookupKey: selected.lookupKey,
            ...(selected.promoEligible && promoCode.trim() && { promoCode: promoCode.trim() }),
          })
        }>
        {checkout.isPending ? 'Opening checkout…' : ctaLabel}
      </Button>

      {checkout.isError && (
        <Typography role="alert" variant="caption" color="error" textAlign="center">
          {errorMessage ?? 'Could not start checkout. Please try again.'}
        </Typography>
      )}

      <Typography color="text.secondary" textAlign="center" sx={{ fontSize: 11 }}>
        Need more than 5 homes? Contact us about enterprise pricing.
      </Typography>
    </Stack>
  );
};
